import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, HardDrive, RotateCw, ScrollText, Server } from "lucide-react";
import { useLatestJobResult } from "../lib/jobs.ts";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Dot, ErrorState, toneColor, type Tone } from "../components/Ui.tsx";
import { JobAction } from "../components/JobAction.tsx";
import { fmtAge } from "../lib/time.ts";

/**
 * VPS ops paneli.
 *
 * ⚠ nginx listede var ama yalnızca durum/restart için — hiçbir iş nginx
 * yapılandırmasını değiştirmez. 443'teki default_server tuzağı elle, göz önünde
 * yönetilmeye devam ediyor (bkz. docs/ARCHITECTURE.md).
 *
 * Servis restart'ı "click" seviyesinde (saniyeler içinde geri gelir); onay seviyesi
 * elle değil, paylaşılan job tanımından okunur — JobAction bunu yapıyor.
 */

interface Metrics {
  disk: { size?: string; used?: string; avail?: string; usePct?: string };
  memory: { totalMb: number; freeMb: number };
  load: number[];
  warnings: string[];
}

interface BackupResult {
  path: string;
  ageHours: number;
  sizeBytes: number;
  fresh: boolean;
}

const SERVICES = ["stockapi", "wordulary", "postgresql", "postgrest", "nginx", "zapret"] as const;

export function OpsPage(): ReactNode {
  const navigate = useNavigate();

  const services = useLatestJobResult<Record<string, { active?: string; since?: string }>>(
    "vps.service.status",
  );
  const metrics = useLatestJobResult<Metrics>("vps.metrics");
  const backup = useLatestJobResult<BackupResult>("vps.backup.verify");

  // Ölçüm yoksa değer de yok: 0 yazıp yeşil göstermek "her şey yolunda" demek olurdu.
  const m = metrics.data;
  const diskPct = m?.disk.usePct ? Number(m.disk.usePct.replace("%", "")) : null;
  const memUsed = m ? m.memory.totalMb - m.memory.freeMb : null;
  const memPct = m && memUsed !== null ? Math.round((memUsed / m.memory.totalMb) * 100) : null;
  const load1 = m?.load[0] ?? null;

  const refreshErrors = [services.error, metrics.error, backup.error].filter(Boolean) as string[];

  return (
    <>
      <PageHeader
        kicker="sistem"
        title="Sunucu"
        meta={metrics.job ? `son ölçüm ${fmtAge(Date.now() - new Date(metrics.job.createdAt).getTime())}` : undefined}
        actions={
          <>
            <Button icon={Gauge} loading={metrics.refreshing} onClick={() => void metrics.refresh()}>
              Kaynakları ölç
            </Button>
            <Button
              variant="primary"
              icon={RotateCw}
              loading={services.refreshing}
              onClick={() => void services.refresh()}
            >
              Servisleri kontrol et
            </Button>
          </>
        }
      />

      <PageBody>
        {refreshErrors.map((e) => (
          <Banner key={e} tone="danger">
            {e}
          </Banner>
        ))}
        {metrics.data?.warnings.length ? (
          <Banner kicker="eşik">{metrics.data.warnings.join(" · ")}</Banner>
        ) : null}

        {metrics.loadError ? (
          <Card flush>
            <ErrorState error={metrics.loadError} onRetry={metrics.reload} />
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Meter
              label="Disk"
              value={m?.disk.usePct ?? "—"}
              pct={diskPct ?? 0}
              note={m ? `${m.disk.used} / ${m.disk.size} · ${m.disk.avail} boş` : noData(metrics.isLoading)}
              tone={diskPct === null ? "idle" : diskPct > 85 ? "danger" : diskPct > 70 ? "warn" : "ok"}
            />
            <Meter
              label="RAM"
              value={memPct === null ? "—" : `%${memPct}`}
              pct={memPct ?? 0}
              note={m && memUsed !== null ? `${memUsed} / ${m.memory.totalMb} MB` : noData(metrics.isLoading)}
              tone={memPct === null ? "idle" : memPct > 88 ? "danger" : memPct > 75 ? "warn" : "ok"}
            />
            <Meter
              label="Yük"
              value={load1 === null ? "—" : load1.toFixed(2)}
              pct={load1 === null ? 0 : Math.min(100, (load1 / 4) * 100)}
              note={load1 === null ? noData(metrics.isLoading) : "4 çekirdek"}
              tone={load1 === null ? "idle" : load1 > 3.5 ? "danger" : load1 > 2 ? "warn" : "ok"}
            />
            <Meter
              label="Yedek"
              value={backup.data ? `${backup.data.ageHours.toFixed(0)} sa` : backup.loadError ? "?" : "—"}
              pct={backup.data ? Math.min(100, (backup.data.ageHours / 36) * 100) : 0}
              note={
                backup.loadError
                  ? "okunamadı"
                  : backup.data
                    ? `${(backup.data.sizeBytes / 1024 / 1024).toFixed(1)} MB · eşik 36 sa`
                    : backup.isLoading
                      ? "yükleniyor…"
                      : "doğrulama yok"
              }
              tone={backup.loadError ? "danger" : backup.data ? (backup.data.fresh ? "ok" : "danger") : "idle"}
            />
          </div>
        )}

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <Card
            title="systemd servisleri"
            icon={Server}
            hint={services.job ? fmtAge(Date.now() - new Date(services.job.createdAt).getTime()) : undefined}
            flush
          >
            {services.loadError ? (
              <ErrorState error={services.loadError} onRetry={services.reload} />
            ) : (
              <>
                {!services.data && (
                  <p className="px-4 pt-3 text-2xs text-neutral-500">
                    {services.isLoading
                      ? "Yükleniyor…"
                      : "Henüz servis kontrolü çalışmadı — üstteki düğmeyle başlat."}
                  </p>
                )}
                {SERVICES.map((name) => {
                  const info = services.data?.[name];
                  const ok = String(info?.active ?? "").startsWith("active");
                  return (
                    <div key={name} className="row">
                      <Dot tone={info ? (ok ? "ok" : "danger") : "idle"} />
                      <div className="grid min-w-0 gap-0.5">
                        <span className="mono text-xs text-neutral-100">{name}</span>
                        <span className="text-2xs text-neutral-600">{info?.active ?? "bilinmiyor"}</span>
                      </div>
                      <JobAction
                        type="vps.service.restart"
                        params={{ service: name }}
                        label={`${name} yeniden başlatma`}
                        variant="ghost"
                        icon={RotateCw}
                        className="!ml-auto"
                        onDone={() => services.refresh()}
                      >
                        Yeniden başlat
                      </JobAction>
                    </div>
                  );
                })}
              </>
            )}
          </Card>

          <div className="grid min-w-0 gap-4">
            <Card title="Yedek doğrulama" icon={HardDrive}>
              <div className="grid gap-2.5">
                {backup.loadError ? (
                  <ErrorState error={backup.loadError} onRetry={backup.reload} compact />
                ) : backup.data ? (
                  <span
                    className="text-sm"
                    style={{ color: backup.data.fresh ? "var(--color-ok)" : "var(--color-danger)" }}
                  >
                    {backup.data.ageHours.toFixed(1)} sa önce · eşik 36 sa ·{" "}
                    {backup.data.fresh ? "taze" : "ESKİ"}
                  </span>
                ) : (
                  <span className="text-sm text-neutral-500">
                    {backup.isLoading ? "Yükleniyor…" : "Henüz doğrulama çalışmadı"}
                  </span>
                )}
                <div>
                  <Button loading={backup.refreshing} onClick={() => void backup.refresh()}>
                    Şimdi doğrula
                  </Button>
                </div>
              </div>
            </Card>

            <Card title="Servis logu" icon={ScrollText} hint="canlı akış ayrı bir iş olarak açılır" flush>
              <div className="grid gap-2.5 p-4">
                <p className="text-xs text-neutral-500">
                  journalctl akışı uzun ömürlü bir iştir; başlattığında iş sayfasında canlı
                  olarak akar ve iptal edene kadar açık kalır.
                </p>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map((name) => (
                    <JobAction
                      key={name}
                      type="vps.journal.tail"
                      params={{ service: name, lines: 200 }}
                      label={`${name} logu`}
                      wait={false}
                      quiet
                      onDone={(job) => navigate(`/jobs/${job.id}`)}
                    >
                      {name}
                    </JobAction>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function noData(loading: boolean): string {
  return loading ? "yükleniyor…" : "ölçüm yok";
}

/** Ölçüm kutusu: değer + ince oran çubuğu + açıklama. */
function Meter({
  label,
  value,
  pct,
  note,
  tone,
}: {
  label: string;
  value: string;
  pct: number;
  note: string;
  tone: Tone;
}): ReactNode {
  return (
    <div className="card grid min-w-0 gap-2 px-4 py-3">
      <div className="flex items-baseline gap-2">
        <span className="kicker">{label}</span>
        <span
          className="mono ml-auto text-lg font-medium"
          style={{ color: toneColor[tone], fontFamily: "var(--font-heading)" }}
        >
          {value}
        </span>
      </div>
      <div className="h-[3px] overflow-hidden rounded-[2px] bg-neutral-900">
        <div
          className="h-[3px]"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: toneColor[tone] }}
        />
      </div>
      <span className="truncate text-2xs text-neutral-500">{note}</span>
    </div>
  );
}
